import type { ReactNode } from 'react';
import React, { createContext, useMemo, useState } from 'react';

export interface GlobalStorageContextProps {
  homeownerAppUploadFile?: File;
  setHomeownerAppUploadFile: (file?: File) => void;
  homeownerAppSpaceType?: string;
  setHomeownerAppSpaceType: (spaceType?: string) => void;
  homeownerAppIsUserHomeowner: boolean;
  setHomeownerAppIsUserHomeowner: (isHomeowner: boolean) => void;
}

const globalStorageContext = createContext<GlobalStorageContextProps>({
  homeownerAppUploadFile: undefined,
  setHomeownerAppUploadFile: () => undefined,
  homeownerAppSpaceType: undefined,
  setHomeownerAppSpaceType: () => undefined,
  homeownerAppIsUserHomeowner: false,
  setHomeownerAppIsUserHomeowner: () => undefined,
});

/**
 * Provider for global storage context
 * Due to the navigation to log in/sign up we need a place to store files
 * at a top level of the app.
 *
 * Similar use cases should be placed in this provider too.
 */
const Provider = ({ children }: { children: ReactNode }) => {
  const [homeownerAppUploadFile, setHomeownerAppUploadFile] = useState<
    File | undefined
  >();
  const [homeownerAppSpaceType, setHomeownerAppSpaceType] = useState<
    string | undefined
  >();
  const [homeownerAppIsUserHomeowner, setHomeownerAppIsUserHomeowner] =
    useState(false);
  const value = useMemo(
    () => ({
      homeownerAppUploadFile,
      setHomeownerAppUploadFile,
      homeownerAppSpaceType,
      setHomeownerAppSpaceType,
      homeownerAppIsUserHomeowner,
      setHomeownerAppIsUserHomeowner,
    }),
    [
      homeownerAppUploadFile,
      homeownerAppSpaceType,
      homeownerAppIsUserHomeowner,
    ],
  );
  return (
    <globalStorageContext.Provider value={value}>
      {children}
    </globalStorageContext.Provider>
  );
};

export { Provider, globalStorageContext };
